import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Star, Upload, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WriteReview = ({ isOpen, onClose, productId, productName, onSuccess }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [review, setReview] = useState('');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast({
        title: t('productReviews.write.ratingRequired'),
        description: t('productReviews.write.ratingRequiredDesc'),
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.post(
        `${API}/reviews`,
        {
          productId,
          rating,
          title,
          review,
          photos
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const reward = res.data?.reward_coupon;
      toast({
        title: t('productReviews.write.submitted'),
        description: reward
          ? t('productReviews.write.rewardDesc', { code: reward })
          : t('productReviews.write.submittedDesc'),
        duration: reward ? 60000 : undefined,
      });

      // Reset form
      setRating(0);
      setTitle('');
      setReview('');
      setPhotos([]);

      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Submit review error:', error);
      const errorMsg = error.response?.data?.detail || t('productReviews.write.submitFailed');
      toast({
        title: t('common.error'),
        description: errorMsg,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, i) => {
      const starValue = i + 1;
      return (
        <button
          key={i}
          type="button"
          onClick={() => setRating(starValue)}
          onMouseEnter={() => setHoverRating(starValue)}
          onMouseLeave={() => setHoverRating(0)}
          aria-label={t('productReviews.write.outOfStars', { rating: starValue })}
          className="transition-transform hover:scale-110"
        >
          <Star
            className={`w-8 h-8 ${
              starValue <= (hoverRating || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      );
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('productReviews.write.title')}</DialogTitle>
          <DialogDescription>{productName}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('productReviews.write.rating')} *
            </label>
            <div className="flex gap-1">
              {renderStars()}
            </div>
            {rating > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                {t('productReviews.write.outOfStars', { rating })}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              {t('productReviews.write.reviewTitle')} *
            </label>
            <Input
              id="title"
              placeholder={t('productReviews.write.reviewTitlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              required
            />
          </div>

          <div>
            <label htmlFor="review" className="block text-sm font-medium text-gray-700 mb-2">
              {t('productReviews.write.yourReview')} *
            </label>
            <Textarea
              id="review"
              placeholder={t('productReviews.write.yourReviewPlaceholder')}
              value={review}
              onChange={(e) => setReview(e.target.value)}
              rows={6}
              maxLength={2000}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('productReviews.write.charactersUsed', { used: review.length, max: 2000 })}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-700 mb-2">
              <strong>{t('productReviews.write.tipsTitle')}</strong>
            </p>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>{t('productReviews.write.tip1')}</li>
              <li>{t('productReviews.write.tip2')}</li>
              <li>{t('productReviews.write.tip3')}</li>
            </ul>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              {t('productReviews.write.cancel')}
            </Button>
            <Button
              type="submit"
              className="bg-[#be1f1a] hover:bg-[#a71b17]"
              disabled={loading || rating === 0}
            >
              {loading ? t('productReviews.write.submitting') : t('productReviews.write.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WriteReview;
